import { Text } from '@linktr.ee/arbor/components/Text';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AuthDivider from '@shared/components/AuthDivider';
import AuthForm from '@shared/components/AuthForm';
import AuthLink from '@shared/components/AuthLink';
import Button from '@shared/components/Button';
import { CookiePrefs } from '@shared/components/CookiePrefs';
import { InputText } from '@shared/components/InputText';
import Layout from '@shared/components/Layout';
import { LoginError } from '@shared/components/LoginError';
import SocialButton from '@shared/components/SocialButton';
import {
  getAuthIdentifierType,
  publishAuthIdentifierSubmittedEvent,
  publishLoginAttemptedEvent,
  publishLoginErrorEncounteredEvent,
  publishLoginStartedEvent,
} from '@shared/utils/analytics/events';
import {
  usePublishPageViewedEvent,
  usePublishScreenViewedEvent,
} from '@shared/utils/analytics/hooks';
import getPlatformType from '@shared/utils/getPlatformType';
import { isMobileApp } from '@shared/utils/isMobileApp';
import { useIsProfileReferral } from '@shared/utils/useIsProfileReferral';
import { validateEmailOrUsername } from '@shared/utils/validation';
import bannerLoginImage from '@universal-login/images/banner-login-desktop.webp';
import { LoginScreenProps } from '@universal-login/types/descope';

const LINKTREE_BASE_URL =
  import.meta.env.VITE_LINKTREE_BASE_URL || 'https://linktr.ee';

const LoginScreen: React.FC<LoginScreenProps> = ({
  onFormUpdate,
  onClickContinue,
  onClickSMS,
  onClickGoogle,
  onClickApple,
  onClickSignUp,
  showSmsLogin = false,
  onIdentifierSubmitted,
  onEmailOtpAttempted,
  errorText,
  context,
  resetError,
}) => {
  const { t } = useTranslation();
  usePublishPageViewedEvent();
  usePublishScreenViewedEvent('Log In');

  const initialEmailOrUsername = context?.form?.['form.emailOrUsername'];
  const [emailOrUsername, setEmailOrUsername] = useState(
    typeof initialEmailOrUsername === 'string' ? initialEmailOrUsername : ''
  );
  const [identifierError, setIdentifierError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [failCount, setFailCount] = useState(0);
  const [emailErrorCount, setEmailErrorCount] = useState(0);
  const [usernameErrorCount, setUsernameErrorCount] = useState(0);
  const [pageError, setPageError] = useState<
    | {
        error?: string;
        description?: string;
        errorDescription?: string;
      }
    | undefined
  >(undefined);
  const isProfileReferral = useIsProfileReferral();
  const platformType = getPlatformType(isProfileReferral, isMobileApp());
  const hasPublishedLoginStarted = useRef(false);

  const publishLoginStartedOnce = (
    loginMethod: 'Email' | 'SMS' | 'Google' | 'Apple'
  ) => {
    if (hasPublishedLoginStarted.current) {
      return;
    }

    hasPublishedLoginStarted.current = true;
    publishLoginStartedEvent(loginMethod);
  };

  // Handle the backend validation errors returned from Descope
  useEffect(() => {
    if (errorText && errorText !== pageError?.error) {
      setPageError({ error: errorText });
      setFailCount(prev => prev + 1);

      // Update specific error counts based on error type
      if (errorText.toLowerCase().includes('email')) {
        setEmailErrorCount(prev => prev + 1);
        if (errorText === "Let's double-check your email.") {
          setIdentifierError('invalid_email');
        }
      } else if (errorText.toLowerCase().includes('username')) {
        setUsernameErrorCount(prev => prev + 1);
        if (errorText === "Let's double-check your username.") {
          setIdentifierError('invalid_username');
        }
      }
    }
  }, [errorText, pageError?.error]);

  const handleIdentifierChange = (value: string) => {
    const trimmedValue = value.trim();

    if (trimmedValue.length > 0) {
      publishLoginStartedOnce('Email');
    }

    // Reset all error when user starts typing
    resetError();
    setIdentifierError(undefined);
    setPageError(undefined);

    // Update identifier
    setEmailOrUsername(trimmedValue);
    onFormUpdate({ 'form.emailOrUsername': trimmedValue });
  };

  const handleEmailOTPLogin = async (
    e?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    options: { publishAttempt?: boolean } = {}
  ) => {
    e?.preventDefault();

    if (!emailOrUsername) {
      setIdentifierError('identifier_required');
      return;
    }

    publishLoginStartedOnce('Email');

    // Validate before submit
    const validation = validateEmailOrUsername(emailOrUsername);
    if (!validation.isValid) {
      setIdentifierError(validation.errorCode);

      // Update error counts based on validation type
      if (emailOrUsername.includes('@')) {
        setEmailErrorCount(prev => prev + 1);
      } else {
        setUsernameErrorCount(prev => prev + 1);
      }
      setFailCount(prev => prev + 1);

      return;
    }

    setIsLoading(true);

    try {
      publishAuthIdentifierSubmittedEvent(
        'login',
        getAuthIdentifierType(emailOrUsername)
      );
      if (options.publishAttempt) {
        publishLoginAttemptedEvent('Email OTP', platformType);
        onEmailOtpAttempted?.();
      } else {
        onIdentifierSubmitted?.();
      }

      const formData = { 'form.emailOrUsername': emailOrUsername };
      onFormUpdate(formData);
      await onClickContinue();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Login failed';
      publishLoginErrorEncounteredEvent('Email OTP', errorMessage);
      setPageError({ error: errorMessage });

      // Update error counts based on input type
      if (emailOrUsername.includes('@')) {
        setEmailErrorCount(prev => prev + 1);
      } else {
        setUsernameErrorCount(prev => prev + 1);
      }
      setFailCount(prev => prev + 1);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailOtpFlow = async () => {
    // Reset error counts when switching to OTP flow
    setIdentifierError(undefined);
    setFailCount(0);
    setEmailErrorCount(0);
    setUsernameErrorCount(0);
    setPageError(undefined);

    // Trigger OTP flow
    await handleEmailOTPLogin(undefined, { publishAttempt: true });
  };

  // Redirect to signup page
  const handleSignUp = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClickSignUp();
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      publishLoginStartedOnce('Google');
      publishLoginAttemptedEvent('Google', platformType);
      await onClickGoogle(); // Google login interaction
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Google login failed';
      publishLoginErrorEncounteredEvent('Google', errorMessage);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSmsLogin = async () => {
    if (!onClickSMS) {
      return;
    }

    setSmsLoading(true);
    try {
      publishLoginStartedOnce('SMS');
      publishLoginAttemptedEvent('SMS', platformType);
      await onClickSMS();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'SMS login failed';
      publishLoginErrorEncounteredEvent('SMS', errorMessage);
    } finally {
      setSmsLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setAppleLoading(true);
    try {
      publishLoginStartedOnce('Apple');
      publishLoginAttemptedEvent('Apple', platformType);
      await onClickApple(); // Apple login interaction
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Apple login failed';
      publishLoginErrorEncounteredEvent('Apple', errorMessage);
    } finally {
      setAppleLoading(false);
    }
  };

  const isFormValid = emailOrUsername.length > 0;

  return (
    <Layout
      heading={t('Welcome back')}
      subHeading={t('Log in to your Linktree')}
      variant="banner"
      bannerImageDesktop={bannerLoginImage}
    >
      <div className="space-y-6">
        <LoginError
          identifierError={identifierError}
          pageError={pageError}
          failCount={failCount}
          emailErrorCount={emailErrorCount}
          usernameErrorCount={usernameErrorCount}
          onTriggerOtpFlow={handleEmailOtpFlow}
          handleSignUp={handleSignUp}
        />

        <AuthForm>
          <InputText
            label={t('Email or username')}
            value={emailOrUsername}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleIdentifierChange(e.target.value);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter' && isFormValid && !isLoading) {
                e.preventDefault();
                const mockEvent = {
                  preventDefault: () => e.preventDefault(),
                } as React.MouseEvent<HTMLButtonElement>;
                handleEmailOTPLogin(mockEvent);
              }
            }}
            autoCapitalize="none"
          />
          <Button
            onClick={handleEmailOTPLogin}
            variant="primary"
            label={t('Continue')}
            disabled={!isFormValid}
            state={isLoading ? 'loading' : 'default'}
          />
        </AuthForm>

        <AuthDivider />

        <div className="space-y-3">
          <SocialButton
            provider="google"
            onClick={handleGoogleLogin}
            loading={googleLoading}
          >
            {t('Continue with Google')}
          </SocialButton>
          <SocialButton
            provider="apple"
            onClick={handleAppleLogin}
            loading={appleLoading}
          >
            {t('Continue with Apple')}
          </SocialButton>
          {showSmsLogin && (
            <Button
              onClick={handleSmsLogin}
              label={t('Continue with phone number')}
              state={smsLoading ? 'loading' : 'default'}
            />
          )}
        </div>

        <div className="text-center mt-8">
          <div className="flex justify-center items-center py-6 text-center">
            <AuthLink>
              <a href={`${LINKTREE_BASE_URL}/request`}>
                {t('Forgot password?')}
              </a>
            </AuthLink>
            <span className="mx-1">•</span>
            <AuthLink>
              <a href={`${LINKTREE_BASE_URL}/forgot-username`}>
                {t('Forgot username?')}
              </a>
            </AuthLink>
          </div>
          <Text as="p" variant="body-small-regular" className="text-gray-600">
            {t("Don't have an account?")}{' '}
            <AuthLink>
              <button type="button" onClick={handleSignUp}>
                {t('Sign up')}
              </button>
            </AuthLink>
          </Text>
        </div>
      </div>
      <CookiePrefs />
    </Layout>
  );
};

export default LoginScreen;
